import React, {
  useState,
  ChangeEvent,
  FormEvent,
  useContext,
  useCallback,
  useMemo,
} from "react";
import Form from "react-bootstrap/Form";
import Button from "react-bootstrap/Button";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import Stack from "react-bootstrap/Stack";
import InputGroup from "react-bootstrap/InputGroup";
import Container from "react-bootstrap/Container";
import ImageCarousel from "../../components/image-carousel";
import { UserAuthenticatedContext } from "../../components/profile";
import ImageUploadComponent, {
  FileWithComment,
} from "../../components/inventory/image-upload-component";
import { GenerateUploadImageUrlImage } from "../../workers/inventory";
import { createWorkerFactory, useWorker } from "@shopify/react-web-worker";
import { getDateString } from "../../helpers/datetime";
import Alert from "react-bootstrap/Alert";
import ProgressBar from "react-bootstrap/ProgressBar";

export interface ChangeFormProps {
  initialValues?: Partial<ProductDataViewerChangeFormState>;
  originatingPMID: string;
  currentPMSheetId: string;
  onSubmitSuccess?: () => void;
}

export interface ProductDataViewerChangeFormState {
  sku: string;
  precreated: string;
  originatingPMID: string;
  title: string;
  boxes: number;
  kgs: number;
  rrp: number;
  boxL: number;
  boxW: number;
  boxH: number;
  description: string;
  existingImageURLs: string[];
  newImages: File[];
}

const initialState: ProductDataViewerChangeFormState = {
  sku: "",
  precreated: "",
  originatingPMID: "",
  title: "",
  boxes: 1,
  kgs: 0,
  rrp: 0,
  boxL: 0,
  boxW: 0,
  boxH: 0,
  description: "",
  existingImageURLs: [],
  newImages: [],
};

const createImageWorker = createWorkerFactory(
  () => import("../../workers/images")
);

const createPMUIWorker = createWorkerFactory(
  () => import("../../workers/pmui")
);

// Create a memoized image carousel component to prevent re-renders
const MemoizedImageCarousel = React.memo(
  ({
    imageUrls,
    maxWidth,
    maxHeight,
  }: {
    imageUrls: string[];
    maxWidth: string;
    maxHeight: string;
  }) => (
    <ImageCarousel
      maxWidth={maxWidth}
      maxHeight={maxHeight}
      imageUrls={imageUrls}
    />
  )
);

// Create a memoized image upload component
const MemoizedImageUpload = React.memo(
  ({
    onDrop,
    hideCommentInput,
    maxFiles,
    disabled,
  }: {
    onDrop: (files: FileWithComment[]) => void;
    hideCommentInput: boolean;
    maxFiles: number;
    disabled?: boolean;
  }) => (
    <ImageUploadComponent
      hideCommentInput={hideCommentInput}
      maxFiles={maxFiles}
      onDrop={onDrop}
      isUploading={disabled}
    />
  )
);

const ChangeForm: React.FC<ChangeFormProps> = ({
  initialValues = {},
  onSubmitSuccess,
  originatingPMID,
  currentPMSheetId,
}) => {
  const [form, setForm] = useState<ProductDataViewerChangeFormState>({
    ...initialState,
    ...initialValues,
  });
  const [submitting, setSubmitting] = useState(false);
  const [isUploadingImages, setIsUploadingImages] = useState(false);
  // New states for upload progress
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadStatus, setUploadStatus] = useState("");
  const [totalUploads, setTotalUploads] = useState(0);
  const [completedUploads, setCompletedUploads] = useState(0);

  const userAuthContext = useContext(UserAuthenticatedContext);

  const currentCbm = useMemo(() => {
    return (form.boxL * form.boxW * form.boxH) / 1000000;
  }, [form.boxL, form.boxW, form.boxH]);

  const [imagesToUpload, setImagesToUpload] = useState<FileWithComment[]>([]);
  const imageWorker = useWorker(createImageWorker);
  const pmuiWorker = useWorker(createPMUIWorker);

  const handleChange = useCallback(
    (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      const { name, value } = e.target;
      setForm((prev) => ({ ...prev, [name]: value }));
    },
    []
  );

  const handleImageUpload = useCallback((files: FileWithComment[]) => {
    setImagesToUpload(files);
  }, []);

  const handleSubmit = useCallback(
    async (e: FormEvent<HTMLFormElement>) => {
      e.preventDefault();

      if (!userAuthContext.token) {
        console.error("User is not authenticated. Cannot submit form.");
        return;
      }
      setSubmitting(true);

      let imageUrls: string[] = [];
      if (imagesToUpload.length > 0) {
        setIsUploadingImages(true);
        setUploadStatus("Preparing images for upload...");
        setUploadProgress(0);
        setTotalUploads(imagesToUpload.length);
        setCompletedUploads(0);

        const images: GenerateUploadImageUrlImage[] = imagesToUpload.map(
          (image) => ({
            name: image.file.name,
            imageSource: "upload",
          })
        );

        try {
          const dateNow = new Date();
          const filenameDate = getDateString(dateNow);

          setUploadStatus("Generating upload URLs...");
          const presignedUrlsResponse =
            await imageWorker.GetPresignedUrlForGenericEntityImagesUpload(
              "catalogue-request",
              [
                "catalogue-request",
                form.sku,
                originatingPMID,
                filenameDate,
                dateNow.getTime(),
              ].join("-"),
              images
            );

          setUploadStatus(`Uploading ${imagesToUpload.length} image(s)...`);

          // Upload images sequentially to show accurate progress
          for (let i = 0; i < presignedUrlsResponse.presignedUrls.length; i++) {
            const response = presignedUrlsResponse.presignedUrls[i];
            const image = imagesToUpload[i];

            setUploadStatus(
              `Uploading image ${i + 1} of ${imagesToUpload.length}: ${
                image.file.name
              }`
            );

            await imageWorker.UploadFileViaPresignedUrl(
              response.url,
              image.file,
              image.file.type
            );

            setCompletedUploads((prev) => prev + 1);
            setUploadProgress(
              Math.round(((i + 1) / imagesToUpload.length) * 100)
            );
          }

          setUploadStatus("All images uploaded successfully!");

          let baseUrl = window.location.origin;
          // For dev purposes, use a specific base URL for image uploads
          if (window.location.hostname === "localhost") {
            baseUrl = "https://circle.dev.clearcyclecloud.uk";
          }
          imageUrls = presignedUrlsResponse.presignedUrls.map(
            (p) => `${baseUrl}/images/inventory/${p.imageKey}`
          );
          console.debug("Uploaded images:", imageUrls);
        } catch (error) {
          setUploadStatus(
            `Error uploading images: ${
              error instanceof Error ? error.message : "Unknown error"
            }`
          );
          console.error("Error uploading images:", error);
          setSubmitting(false);
          setIsUploadingImages(false);
          return;
        }
      }

      setUploadStatus("Submitting form data...");

      try {
        await pmuiWorker.putCRAPRow(userAuthContext.token, {
          sku: form.sku,
          boxes: form.boxes,
          boxH: form.boxH,
          boxW: form.boxW,
          boxD: form.boxL,
          description: form.description,
          pmDocID: currentPMSheetId,
          rrp: form.rrp,
          totalKgs: form.kgs,
          title: form.title,
          preCreated: form.precreated,
          userEmail: userAuthContext.user?.email ?? "unknown",
          imageUrls: imageUrls,
        });

        setUploadStatus("Request submitted successfully!");

        // Call the onSubmitSuccess callback if provided
        if (onSubmitSuccess) {
          onSubmitSuccess();
        }
      } catch (error) {
        setUploadStatus(
          `Error submitting form: ${
            error instanceof Error ? error.message : "Unknown error"
          }`
        );
        console.error("Error submitting form:", error);
      } finally {
        setSubmitting(false);
        setIsUploadingImages(false);
      }
    },
    [
      form,
      imagesToUpload,
      imageWorker,
      onSubmitSuccess,
      originatingPMID,
      pmuiWorker,
      userAuthContext,
      currentPMSheetId,
    ]
  );

  // Form is processing if either submitting or uploading images
  const isProcessing = submitting || isUploadingImages;

  // Memoize image URLs array to prevent unnecessary re-renders
  const existingImageUrls = useMemo(
    () => form.existingImageURLs,
    [form.existingImageURLs]
  );

  return (
    <Form onSubmit={handleSubmit} style={{ padding: 10 }}>
      {/* Show upload status if we're processing */}
      {isProcessing && (
        <Alert variant="info" className="mb-3">
          <Alert.Heading>Processing Request</Alert.Heading>
          <p>{uploadStatus}</p>
          {totalUploads > 0 && (
            <>
              <ProgressBar
                animated
                now={uploadProgress}
                label={`${completedUploads}/${totalUploads} images`}
                className="mb-2"
              />
              <p className="mb-0">
                <small>
                  Please don't close this window until the request is complete.
                </small>
              </p>
            </>
          )}
        </Alert>
      )}

      {/* Rest of the form - disable all inputs during processing */}
      <Row>
        <Col md={6} className="mb-2">
          <Form.Group>
            <Form.Label>SKU</Form.Label>
            <Form.Control
              type="text"
              name="sku"
              value={form.sku}
              readOnly
              disabled
            />
            <Form.Control
              type="hidden"
              name="precreated"
              value={form.precreated}
            />
            <Form.Control
              type="hidden"
              name="originatingpmid"
              value={form.originatingPMID}
            />
            <Form.Control
              type="hidden"
              name="user"
              value={userAuthContext.user?.email}
            />
          </Form.Group>
        </Col>
      </Row>
      <Row>
        <Col md={12} className="mb-2">
          <Form.Group>
            <Form.Label>Product Title</Form.Label>
            <Form.Control
              type="text"
              name="title"
              value={form.title}
              onChange={handleChange}
              disabled={isProcessing}
            />
          </Form.Group>
        </Col>
      </Row>
      <Row>
        <Col md={4} className="mb-2">
          <Form.Group>
            <Form.Label>Number of Boxes</Form.Label>
            <Form.Control
              type="text"
              name="boxes"
              value={form.boxes}
              onChange={handleChange}
              disabled={isProcessing}
            />
          </Form.Group>
        </Col>
        <Col md={4} className="mb-2">
          <Form.Label>Weight (kgs)</Form.Label>
          <InputGroup>
            <Form.Control
              type="text"
              name="kgs"
              value={form.kgs}
              onChange={handleChange}
              disabled={isProcessing}
            />
            <InputGroup.Text>cm</InputGroup.Text>
          </InputGroup>
        </Col>
        <Col md={4} className="mb-2">
          <Form.Label>RRP</Form.Label>
          <InputGroup>
            <InputGroup.Text>£</InputGroup.Text>
            <Form.Control
              type="text"
              name="rrp"
              value={form.rrp}
              onChange={handleChange}
              disabled={isProcessing}
            />
          </InputGroup>
        </Col>
      </Row>
      <Row>
        <Col md={4} className="mb-2">
          <Form.Label>Average Box Length/Depth</Form.Label>
          <InputGroup>
            <Form.Control
              type="text"
              name="boxL"
              value={form.boxL}
              onChange={handleChange}
              disabled={isProcessing}
            />
            <InputGroup.Text>cm</InputGroup.Text>
          </InputGroup>
        </Col>
        <Col md={4} className="mb-2">
          <Form.Label>Average Box Width</Form.Label>
          <InputGroup>
            <Form.Control
              type="text"
              name="boxW"
              value={form.boxW}
              onChange={handleChange}
              disabled={isProcessing}
            />
            <InputGroup.Text>cm</InputGroup.Text>
          </InputGroup>
        </Col>
        <Col md={4} className="mb-2">
          <Form.Label>Average Box Height</Form.Label>
          <InputGroup>
            <Form.Control
              type="text"
              name="boxH"
              value={form.boxH}
              onChange={handleChange}
              disabled={isProcessing}
            />
            <InputGroup.Text>cm</InputGroup.Text>
          </InputGroup>
        </Col>
        <Col md={4} className="mb-2">
          <Form.Label>Calculated Cbm</Form.Label>
          <InputGroup>
            <Form.Control
              type="text"
              name="boxH"
              value={currentCbm.toFixed(5)}
              readOnly
              disabled
              isInvalid={
                currentCbm <= 0 || isNaN(currentCbm) || currentCbm > 20
              }
            />
            <InputGroup.Text>m³</InputGroup.Text>
          </InputGroup>
        </Col>
        <Col md={8} className="mb-2">
          <Stack direction="vertical">
            <Form.Label>Existing Measurements</Form.Label>
            <Stack className="mt-1" direction="horizontal" gap={3}>
              <span>Boxes: {initialValues.boxes}</span>
              <span>Weight: {initialValues.kgs} kg</span>
              <span>
                m³:{" "}
                {initialValues.boxH && initialValues.boxW && initialValues.boxL
                  ? (
                      (initialValues.boxL *
                        initialValues.boxW *
                        initialValues.boxH) /
                      1000000
                    ).toFixed(5)
                  : "N/A"}
              </span>
            </Stack>
            <span>
              Dimensions: {initialValues.boxL} x {initialValues.boxW} x{" "}
              {initialValues.boxH} cm
            </span>
          </Stack>
        </Col>
      </Row>
      <Row>
        <Col md={12} className="mb-2">
          <Form.Group>
            <Form.Label>Description</Form.Label>
            {/* <WYSIWYGEditor
              isSaving={false}
              setValue={(e) =>
                handleChange({
                  target: {
                    name: "description",
                    value: e,
                    type: "text",
                  },
                } as ChangeEvent<HTMLInputElement | HTMLTextAreaElement>)
              }
              value={form.description}
              viewOnly={false}
              namespace={form.originatingPMID}
            /> */}
            <Form.Control
              as="textarea"
              rows={5}
              name="description"
              value={form.description}
              onChange={handleChange}
              disabled={isProcessing}
            />
          </Form.Group>
        </Col>
      </Row>
      <Row>
        <Col md={12} className="mb-2">
          <Form.Group>
            <Form.Label>Current Images</Form.Label>
            {existingImageUrls.length === 0 ? (
              <i>No existing images. Please add some below.</i>
            ) : (
              <MemoizedImageCarousel
                maxWidth="100%"
                maxHeight="300px"
                imageUrls={existingImageUrls}
              />
            )}
          </Form.Group>
        </Col>
      </Row>
      <Row>
        <Col lg={12} className="mb-2">
          <Form.Group>
            <Form.Label>Upload New Product Images</Form.Label>
            <Container className="pt-4 pb-4">
              <MemoizedImageUpload
                hideCommentInput={true}
                maxFiles={5}
                onDrop={handleImageUpload}
                disabled={isProcessing}
              />
            </Container>
            <Form.Text className="text-muted">
              Images uploaded should be a true likeness of the product and
              clearly show any damage/repairs. Images need to be in focus, the
              product must be suitably dressed and positioned, the surrounding
              area should be well lit and spotlessly tidy.
            </Form.Text>
          </Form.Group>
        </Col>
      </Row>
      <Row>
        <Col md={12} className="mb-2">
          <Button type="submit" variant="success" disabled={isProcessing}>
            {submitting ? (
              <>
                <span
                  className="spinner-border spinner-border-sm me-2"
                  role="status"
                  aria-hidden="true"
                ></span>
                {imagesToUpload.length > 0
                  ? "Uploading & Submitting..."
                  : "Submitting..."}
              </>
            ) : (
              "Submit Request"
            )}
          </Button>
        </Col>
      </Row>
    </Form>
  );
};

export default React.memo(ChangeForm);
